import React, { useCallback, useEffect, useState } from 'react';
import { Button, Heading, Text, Toggle, Loader } from '@vibe/core';
import {
  getAppSettings,
  updateAppSettings,
} from '../../api/settings.api';

const WorkloadDefaultsPanel: React.FC = () => {
  const [perPersonDistribution, setPerPersonDistribution] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getAppSettings()
      .then((settings) => {
        setPerPersonDistribution(settings.perPersonDistribution ?? false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load settings');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateAppSettings({ perPersonDistribution });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  }, [perPersonDistribution]);

  if (isLoading) {
    return (
      <div className="rp-workload-defaults">
        <div className="rp-workload-defaults__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  return (
    <div className="rp-workload-defaults">
      <Heading type={Heading.types.H3}>Workload Defaults</Heading>
      <Text
        type={Text.types.TEXT2}
        style={{ color: '#676879', marginTop: 4, marginBottom: 16 }}
      >
        Set account-level defaults for workload settings. Individual boards can
        override these values in their board settings.
      </Text>

      <div className="rp-workload-defaults__fields">
        <div className="rp-workload-defaults__field rp-workload-defaults__toggle-row">
          <label className="rp-workload-defaults__label">
            Per-Person Hour Distribution
          </label>
          <Toggle
            isSelected={perPersonDistribution}
            onChange={(checked: boolean) => setPerPersonDistribution(checked)}
            size="small"
            ariaLabel="Per-person hour distribution"
          />
          <p className="rp-workload-defaults__hint">
            When enabled, task hours are divided equally among all assignees for workload calculations.
          </p>
        </div>
      </div>

      {error && <p className="rp-workload-defaults__error">{error}</p>}

      <div className="rp-workload-defaults__actions">
        <Button onClick={handleSave} loading={isSaving}>
          {saved ? 'Saved!' : 'Save Defaults'}
        </Button>
      </div>
    </div>
  );
};

export default WorkloadDefaultsPanel;
